import React from 'react';
import moment from 'moment';
import { DillButton } from '../../../general/Dill';
import {
  InternalFormSignerStep,
  InternalFormSubmission,
  InternalFormSubmissionSignature,
} from '@dill/dill-shared';

type SignerStatus = 'SIGNED' | 'AWAITING' | 'UPCOMING' | 'NOT_REQUIRED';

// Why a required step had nobody to sign it.
const UNSIGNABLE_REASONS: Record<InternalFormSignerStep['source'], string> = {
  BRANCH_TM: 'nobody was found to sign: the branch has no Branch Manager.',
  REGION_RSM: "nobody was found to sign: the branch isn't in a region with a Regional Manager.",
  CREDIT_APP_DECISION_MAKER: 'nobody was found to sign: there are no credit decision makers.',
  EMAILS: 'nobody was found to sign: no emails are set for this signer.',
  SALES_REP: 'nobody was found to sign: the sales rep has no email.',
};

const STATUS_STYLES: Record<SignerStatus, { label: string; className: string }> = {
  SIGNED: { label: 'Signed', className: 'bg-green-50 text-green-700 border-green-200' },
  AWAITING: {
    label: 'Awaiting signature',
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  UPCOMING: { label: 'Upcoming', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  NOT_REQUIRED: { label: 'Not required', className: 'bg-gray-50 text-gray-400 border-gray-200' },
};

type SignerSlot = {
  // Shown under the role: who this is (the person who filled the form in).
  person?: { name?: string | null; email: string };
  key: string;
  role: string;
  status: SignerStatus;
  signature?: InternalFormSubmissionSignature | null;
  // Who can sign, while it's waiting on them.
  pendingEmails?: string[];
  note?: string;
};

// Signer 1 (whoever fills the form in), then each of the template's later signers in order:
// signed ones with their signature, the one the form is waiting on, the ones after it, and any
// passed over because their conditions didn't match (or nobody could be found to sign them).
const buildSignerSlots = (
  submission: Omit<InternalFormSubmission, 'accessToken'>,
  signers: InternalFormSignerStep[]
): SignerSlot[] => {
  const slots: SignerSlot[] = [
    {
      key: 'first',
      role: 'Filled in by',
      person: submission.signature
        ? { name: submission.signature.signedByName, email: submission.signature.signedByEmail }
        : { email: (submission.recipientEmails ?? []).join(', ') },
      status: submission.signature ? 'SIGNED' : 'AWAITING',
      signature: submission.signature,
      pendingEmails: submission.signature ? undefined : submission.recipientEmails,
    },
  ];
  const signedByStep = new Map(
    (submission.signatures ?? []).map((signature) => [signature.stepId ?? '', signature])
  );
  const currentIndex = signers.findIndex((step) => step.id === submission.currentSignerStepId);
  const isStillSigning =
    submission.status === 'SENT' || submission.status === 'AWAITING_SIGNATURES';
  signers.forEach((step, index) => {
    const signature = signedByStep.get(step.id);
    const role = step.label || `Signer ${index + 2}`;
    if (signature) {
      slots.push({ key: step.id, role, status: 'SIGNED', signature });
    } else if (step.id === submission.currentSignerStepId && isStillSigning) {
      slots.push({
        key: step.id,
        role,
        status: 'AWAITING',
        pendingEmails: submission.currentSignerEmails ?? [],
      });
    } else if (
      isStillSigning &&
      (submission.status === 'SENT' || currentIndex === -1 || index > currentIndex)
    ) {
      slots.push({
        key: step.id,
        role,
        status: 'UPCOMING',
        note: 'Signs next if required by the answers',
      });
    } else {
      // A step with nobody to sign it is skipped too (see findNextSignerStep), with a history note
      // — say so rather than "not required", which is wrong and hides a setup gap.
      const wasUnsignable = (submission.history ?? []).some(
        (entry) =>
          entry.notes === `Skipped ${step.label || 'a signer'}: nobody was found to sign it.`
      );
      slots.push({
        key: step.id,
        role,
        status: 'NOT_REQUIRED',
        ...(wasUnsignable ? { note: `Skipped — ${UNSIGNABLE_REASONS[step.source]}` } : {}),
      });
    }
  });
  // Signatures for steps no longer on the template still belong on the record.
  (submission.signatures ?? [])
    .filter((signature) => !signers.some((step) => step.id === signature.stepId))
    .forEach((signature, index) =>
      slots.push({
        key: `removed-${index}`,
        role: signature.stepLabel || 'Signer',
        status: 'SIGNED',
        signature,
      })
    );
  return slots;
};

const InternalFormSignatures = ({
  submission,
  signers,
  onResendEmail,
  isResending = false,
}: {
  submission: Omit<InternalFormSubmission, 'accessToken'>;
  signers: InternalFormSignerStep[];
  // Re-sends the link to whoever the form is waiting on; shown on the awaiting row.
  onResendEmail?: () => void;
  isResending?: boolean;
}) => {
  const slots = buildSignerSlots(submission, signers);
  // One signer per row, in three aligned columns: role and status, the signature (or an empty
  // slot), then who signed or who it's waiting on.
  return (
    <div className="flex flex-col divide-y divide-gray-100">
      {slots.map((slot) => {
        const { signature } = slot;
        const style = STATUS_STYLES[slot.status];
        return (
          <div
            key={slot.key}
            className={`grid grid-cols-1 md:grid-cols-[11rem_14rem_1fr] items-center gap-3 py-3 first:pt-0 last:pb-0 ${
              slot.status === 'NOT_REQUIRED' ? 'opacity-60' : ''
            }`}>
            <div className="flex md:flex-col items-center md:items-start justify-between md:justify-center gap-1.5">
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-gray-900">
                  {slot.role}
                  {slot.person?.name && (
                    <span className="font-normal text-gray-700">{`: ${slot.person.name}`}</span>
                  )}
                </span>
                {slot.person?.email && (
                  <span className="text-[11px] text-gray-500 break-all">{slot.person.email}</span>
                )}
              </div>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap ${style.className}`}>
                {style.label}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <div className="h-12 flex items-center justify-center rounded-md bg-gray-50 border border-dashed border-gray-200 px-2">
                {signature ? (
                  <img
                    src={signature.signature}
                    alt="Signature"
                    className="max-h-9 max-w-full object-contain"
                  />
                ) : (
                  <span className="text-[11px] text-gray-400">Not signed yet</span>
                )}
              </div>
              {signature && (
                <span className="text-[9px] text-gray-400 leading-none">E-signed using Dill</span>
              )}
            </div>
            <div className="flex flex-col gap-0.5 text-xs text-gray-600 min-w-0">
              {signature ? (
                <>
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {signature.signedByName || signature.signedByEmail}
                    {signature.signedByTitle && (
                      <span className="font-normal text-gray-500">{` · ${signature.signedByTitle}`}</span>
                    )}
                  </span>
                  {signature.signedByName && (
                    <span className="truncate">{signature.signedByEmail}</span>
                  )}
                  <span className="text-[11px] text-gray-400">
                    {`Signed ${moment(signature.dateSigned).format('MM/DD/YYYY hh:mm A')}${
                      signature.location ? ` · ${signature.location}` : ''
                    }`}
                  </span>
                </>
              ) : slot.status === 'AWAITING' ? (
                <>
                  <span className="text-[11px] text-gray-400">Waiting on</span>
                  <span className="text-sm text-gray-900 break-all">
                    {(slot.pendingEmails ?? []).join(', ') || '-'}
                  </span>
                  {onResendEmail && (
                    <div className="mt-1.5">
                      <DillButton
                        label="Resend Email"
                        variant="secondary"
                        height={30}
                        isLoading={isResending}
                        isDisabled={isResending}
                        onClick={onResendEmail}
                      />
                    </div>
                  )}
                </>
              ) : slot.status === 'UPCOMING' ? (
                <span>{slot.note}</span>
              ) : (
                <span>{slot.note ?? 'Skipped — not required for these answers.'}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InternalFormSignatures;
